import React, { createContext, useState, useMemo, ReactNode, useEffect, useContext } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { darkTheme, lightTheme } from "./theme";

interface ThemeContextType {
    darkMode: boolean;
    toggleTheme: () => void;
    theme: any;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Read from localStorage on first load
    const [darkMode, setDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme');
        return saved === 'dark';
    });

    useEffect(() => {
        localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    const theme = useMemo(() => (darkMode ? darkTheme : lightTheme), [darkMode]);

    const toggleTheme = () => {
        setDarkMode((prev) => !prev);
    };

    const value = {
        darkMode,
        toggleTheme,
        theme
    };

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
        </ThemeContext.Provider>
    );
}; 